/**
 * Admin pitch "Tour the portal" resolves one firm by name, then uses the
 * existing partner view-as handoff. It does not create firms.
 */

export const PITCH_TOUR_FIRM_NAME = "MCF Test Inc";

const PITCH_RETURN_KEY = "veras.adminPitchReturn";

export type PitchTourFirm = {
  id: string;
  companyName: string;
  isActive?: boolean;
  portalStatus?: string | null;
  hasOwnerSeat?: boolean;
};

export type PitchTourResolution =
  | { ok: true; firm: PitchTourFirm }
  | { ok: false; reason: "missing" | "ambiguous" };

/** Compare names without surrounding space, repeated spaces, or a trailing period. */
export function normalizeFirmName(name: string): string {
  return name.trim().replace(/\s+/g, " ").replace(/\.+$/u, "").toLowerCase();
}

/**
 * Prefer a character-for-character name match. A single firm whose name is
 * the same aside from a trailing period still counts. Several exact matches
 * are ambiguous.
 */
export function resolvePitchTourFirm(
  firms: readonly PitchTourFirm[],
  requestedName = PITCH_TOUR_FIRM_NAME,
): PitchTourResolution {
  const requested = requestedName.trim();
  const exact = firms.filter((firm) => firm.companyName.trim() === requested);
  if (exact.length === 1) return { ok: true, firm: exact[0] };
  if (exact.length > 1) return { ok: false, reason: "ambiguous" };

  const want = normalizeFirmName(requested);
  const normalized = firms.filter((firm) => normalizeFirmName(firm.companyName) === want);
  if (normalized.length === 1) return { ok: true, firm: normalized[0] };
  if (normalized.length > 1) return { ok: false, reason: "ambiguous" };
  return { ok: false, reason: "missing" };
}

/** Same gates the partner roster uses before it calls view-as. */
export function pitchTourBlockReason(firm: PitchTourFirm): string | null {
  if (firm.isActive === false) return "This firm is not active.";
  if (firm.portalStatus && firm.portalStatus !== "approved") {
    return "This firm is still pending approval.";
  }
  if (firm.hasOwnerSeat === false) return "This firm has no account owner yet.";
  return null;
}

export function isPitchReturnPath(path: string | null | undefined): path is string {
  if (!path || !path.startsWith("/admin/partner-pitch")) return false;
  if (path.startsWith("//") || path.includes("\\") || path.includes("://")) return false;
  return true;
}

export function rememberPitchReturn(path: string): void {
  if (!isPitchReturnPath(path) || typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(PITCH_RETURN_KEY, path);
}

export function peekPitchReturn(): string | null {
  if (typeof sessionStorage === "undefined") return null;
  const value = sessionStorage.getItem(PITCH_RETURN_KEY);
  return isPitchReturnPath(value) ? value : null;
}

export function clearPitchReturn(): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(PITCH_RETURN_KEY);
}
